/**
 * seoPaths.js
 *
 * The public address of every storefront and product page, in one place.
 *
 * Search engines learn a page's address from three places: the sitemap (built
 * by the API), the links on our own pages, and the canonical tag the page
 * declares. All three must spell it the same way, or Google treats them as
 * different pages and splits the page's ranking between them. The API mirrors
 * these rules in Lusuku_Dashboard_Node-Server/utils/seo/publicPaths.js, and
 * both sides are pinned by the same test vectors
 * (src/utils/__tests__/seoPaths.test.js here, __tests__/seoCatalog.test.js
 * there).
 *
 * Which id each storefront carries is the id the listing pages already link
 * with, so a tap and a sitemap entry land on the same address:
 *
 *   brand        /brand/shop/<tradingName or name>--<brand _id>
 *   retail       /shop/<name>--<store _id>
 *   restaurant   /restaurant/shop/<name>--<owner id, else _id>
 *   healthcare   /healthcare/shop/<name>--<owner id, else _id>
 *
 * Products: <storefront>/product/<product name>--<product _id>. Restaurant
 * dishes open in a dialog and have no page of their own.
 */
import { buildVendorParam } from "./vendorSlug";

const idOf = (value) => {
  if (value == null) return "";
  if (typeof value === "object" && value._id != null) return String(value._id);
  return String(value);
};

const SHOP_PREFIX = {
  brand: "/brand/shop",
  retail: "/shop",
  restaurant: "/restaurant/shop",
  healthcare: "/healthcare/shop",
};

export const PRODUCT_PAGE_VERTICALS = ["brand", "retail", "healthcare"];

/** The id a vertical's storefront address carries. */
export function shopIdFor(vertical, vendor) {
  if (!vendor) return "";
  if (vertical === "restaurant" || vertical === "healthcare") {
    return idOf(vendor.owner) || idOf(vendor._id);
  }
  return idOf(vendor._id);
}

/** The name a vertical's storefront slug is built from. */
export function shopNameFor(vertical, vendor) {
  if (!vendor) return "";
  if (vertical === "brand") return vendor.tradingName || vendor.name || "";
  return vendor.name || "";
}

/** "/brand/shop/made-in-prc--<id>", or null when the vendor is not loaded yet. */
export function shopPath(vertical, vendor) {
  const prefix = SHOP_PREFIX[vertical];
  const id = shopIdFor(vertical, vendor);
  if (!prefix || !id) return null;
  return `${prefix}/${buildVendorParam(shopNameFor(vertical, vendor), id)}`;
}

/** A product's page under a storefront path the caller already has. */
export function productPathUnder(storefrontPath, product) {
  const id = idOf(product && product._id);
  if (!storefrontPath || !id) return null;
  return `${storefrontPath}/product/${buildVendorParam(product.name, id)}`;
}

/** A product's page from its vendor, or null where the vertical has none. */
export function productPath(vertical, vendor, product) {
  if (!PRODUCT_PAGE_VERTICALS.includes(vertical)) return null;
  return productPathUnder(shopPath(vertical, vendor), product);
}
